import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../services/firebase";
import { COLORS, LAYOUT, CARD_STYLES } from "../constants";
import PurchaseButton from "./PurchaseButton";

// Modern SVG Icons
const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15,18 9,12 15,6"></polyline>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9,18 15,12 9,6"></polyline>
  </svg>
);

const TagIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
    <line x1="7" y1="7" x2="7.01" y2="7"></line>
  </svg>
);

const UserIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

function ListingCard({ listing, user }) {
  const navigate = useNavigate();
  const [imgIndex, setImgIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    if (auth.currentUser) {
      navigate(`/listing/${listing.id}`);
    }
  };

  // Handlers for image navigation
  const showPrev = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setImgIndex((prev) => prev > 0 ? prev - 1 : listing.images.length - 1);
  };
  const showNext = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setImgIndex((prev) => prev < listing.images.length - 1 ? prev + 1 : 0);
  };

  return (
    <div
      className="listing-card"
      style={{
        ...styles.cardContainer,
        transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
        boxShadow: isHovered ? '0 20px 40px rgba(206, 17, 38, 0.15)' : '0 4px 12px rgba(0, 0, 0, 0.08)',
      }}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Enhanced Image Section */}
      {listing.images && listing.images.length > 0 && (
        <div style={styles.imageContainer}>
          <div style={styles.imageWrapper}>
            <img
              className="listing-image"
              src={listing.images[imgIndex]}
              alt={listing.title}
              style={styles.image}
            />
            
            {/* Status Badge */}
            {listing.status === 'sold' && (
              <div style={styles.statusBadge}>
                <span>SOLD</span>
              </div>
            )}
            
            {/* Modern Image Navigation */}
            {listing.images.length > 1 && (
              <>
                <button
                  className="image-nav-button"
                  onClick={showPrev}
                  style={{...styles.navButton, ...styles.navButtonLeft}}
                  aria-label="Previous image"
                >
                  <ChevronLeftIcon />
                </button>
                <button
                  className="image-nav-button"
                  onClick={showNext}
                  style={{...styles.navButton, ...styles.navButtonRight}}
                  aria-label="Next image"
                >
                  <ChevronRightIcon />
                </button>
                
                {/* Enhanced Image Indicators */}
                <div style={styles.indicators}>
                  {listing.images.map((_, index) => (
                    <button
                      key={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        setImgIndex(index);
                      }}
                      style={{
                        ...styles.indicator,
                        backgroundColor: index === imgIndex ? COLORS.primary : 'rgba(255, 255, 255, 0.5)',
                        transform: index === imgIndex ? 'scale(1.2)' : 'scale(1)',
                      }}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
                
                {/* Image Counter */}
                <div style={styles.imageCounter}>
                  {imgIndex + 1} / {listing.images.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Enhanced Content Section */}
      <div style={styles.content}>
        {/* Header Section */}
        <div style={styles.header}>
          <div style={styles.titleSection}>
            <h3 style={styles.title}>{listing.title}</h3>
            <div style={styles.priceContainer}>
              <span style={styles.price}>${listing.price}</span>
              {listing.originalPrice && listing.originalPrice > listing.price && (
                <span style={styles.originalPrice}>${listing.originalPrice}</span>
              )}
            </div>
          </div>
          
          {listing.description && (
            <p style={styles.description}>
              {listing.description.length > 85 
                ? `${listing.description.slice(0, 85)}...` 
                : listing.description}
            </p>
          )}
        </div>

        {/* Metadata Section */}
        <div style={styles.metadata}>
          {/* Category Badge */}
          <div style={styles.categoryBadge}>
            <TagIcon />
            <span>{listing.category}</span>
          </div>

          {/* Condition Badge */}
          {listing.condition && (
            <div style={styles.conditionBadge}>
              <span>{listing.condition}</span>
            </div>
          )}
        </div>

        {/* Seller Section */}
        {listing.seller && (
          <div style={styles.sellerSection}>
            <div style={styles.sellerInfo}>
              <UserIcon />
              <span style={styles.sellerName}>
                {listing.seller.name || 'Anonymous Seller'}
              </span>
            </div>
            {listing.seller.rating && (
              <div style={styles.sellerRating}>
                <span style={styles.ratingStars}>{'★'.repeat(Math.floor(listing.seller.rating))}</span>
                <span style={styles.ratingNumber}>({listing.seller.rating})</span>
              </div>
            )}
          </div>
        )}

        {/* Actions Section */}
        <div style={styles.actions}>
          <PurchaseButton listing={listing} compact={true} />
        </div>
      </div>
    </div>
  );
}

const styles = {
  // Main Card Container
  cardContainer: {
    ...CARD_STYLES.base,
    width: '100%',
    maxWidth: '380px',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: 'auto',
    minHeight: '480px',
    background: COLORS.white,
    border: `1px solid ${COLORS.border}`,
    borderRadius: LAYOUT.borderRadius.xl,
  },

  // Image Section
  imageContainer: {
    position: 'relative',
    width: '100%',
    height: '240px',
    overflow: 'hidden',
    backgroundColor: COLORS.grayLight,
  },
  imageWrapper: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    transition: 'transform 0.3s ease',
    backgroundColor: COLORS.white,
  },
  
  // Status Badge
  statusBadge: {
    position: 'absolute',
    top: LAYOUT.spacing.md,
    left: LAYOUT.spacing.md,
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    padding: `${LAYOUT.spacing.xs} ${LAYOUT.spacing.md}`,
    borderRadius: LAYOUT.borderRadius.sm,
    fontSize: '0.75rem',
    fontWeight: '700',
    letterSpacing: '0.5px',
    zIndex: 3,
  },

  // Navigation Buttons
  navButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    backdropFilter: 'blur(8px)',
    border: `2px solid ${COLORS.primary}`,
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    cursor: 'pointer',
    color: COLORS.text.primary,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    transition: 'all 0.2s ease',
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 1, // Make fully visible for testing
  },
  navButtonLeft: {
    left: LAYOUT.spacing.md,
  },
  navButtonRight: {
    right: LAYOUT.spacing.md,
  },

  // Image Indicators
  indicators: {
    position: 'absolute',
    bottom: LAYOUT.spacing.md,
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: LAYOUT.spacing.xs,
    zIndex: 3,
  },
  indicator: {
    width: '8px',
    height: '8px',
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  },

  // Image Counter
  imageCounter: {
    position: 'absolute',
    top: LAYOUT.spacing.md,
    right: LAYOUT.spacing.md,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: COLORS.white,
    padding: `${LAYOUT.spacing.xs} ${LAYOUT.spacing.sm}`,
    borderRadius: LAYOUT.borderRadius.sm,
    fontSize: '0.75rem',
    fontWeight: '500',
    zIndex: 3,
  },

  // Content Section
  content: {
    padding: LAYOUT.spacing.lg,
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.md,
    flex: 1,
    justifyContent: 'space-between',
  },

  // Header Section
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.sm,
  },
  titleSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: LAYOUT.spacing.md,
    marginBottom: LAYOUT.spacing.xs,
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: '600',
    lineHeight: 1.3,
    color: COLORS.text.primary,
    margin: 0,
    flex: 1,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  priceContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: LAYOUT.spacing.xs,
  },
  price: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: COLORS.primary,
    lineHeight: 1,
  },
  originalPrice: {
    fontSize: '0.875rem',
    color: COLORS.text.muted,
    textDecoration: 'line-through',
    lineHeight: 1,
  },
  description: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: COLORS.text.secondary,
    margin: 0,
  },

  // Metadata Section
  metadata: {
    display: 'flex',
    gap: LAYOUT.spacing.sm,
    flexWrap: 'wrap',
  },
  categoryBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.xs,
    backgroundColor: COLORS.grayLight,
    color: COLORS.text.secondary,
    padding: `${LAYOUT.spacing.xs} ${LAYOUT.spacing.sm}`,
    borderRadius: LAYOUT.borderRadius.md,
    fontSize: '0.75rem',
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  conditionBadge: {
    backgroundColor: COLORS.accent,
    color: COLORS.text.onYellow,
    padding: `${LAYOUT.spacing.xs} ${LAYOUT.spacing.sm}`,
    borderRadius: LAYOUT.borderRadius.md,
    fontSize: '0.75rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  // Seller Section
  sellerSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: LAYOUT.spacing.sm,
    borderTop: `1px solid ${COLORS.border}`,
  },
  sellerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.xs,
  },
  sellerName: {
    fontSize: '0.875rem',
    color: COLORS.text.secondary,
    fontWeight: '500',
  },
  sellerRating: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.xs,
  },
  ratingStars: {
    color: COLORS.accent,
    fontSize: '0.875rem',
  },
  ratingNumber: {
    fontSize: '0.75rem',
    color: COLORS.text.muted,
  },

  // Actions Section
  actions: {
    marginTop: LAYOUT.spacing.md,
  },
};

// Add hover effects and animations
const listingCardStyles = `
  .listing-card .image-nav-button {
    opacity: 1;
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .listing-card:hover .image-nav-button {
    opacity: 1;
  }
  
  .listing-card:hover .listing-image {
    transform: scale(1.02);
  }
  
  .listing-card .image-nav-button:hover {
    background-color: rgba(255, 255, 255, 1) !important;
    transform: translateY(-50%) scale(1.05) !important;
    opacity: 1 !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
  }
  
  .listing-card .image-nav-button:active {
    transform: translateY(-50%) scale(0.98) !important;
    transition: all 0.1s ease !important;
  }
  
  .listing-card .indicator:hover {
    transform: scale(1.3) !important;
  }
  
  @media (max-width: 768px) {
    .listing-card .image-nav-button {
      opacity: 0.9;
    }
    
    .listing-card .image-nav-button:active {
      opacity: 1;
      background-color: rgba(255, 255, 255, 1) !important;
    }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.getElementById('listing-card-styles');
  if (!styleElement) {
    const style = document.createElement('style');
    style.id = 'listing-card-styles';
    style.textContent = listingCardStyles;
    document.head.appendChild(style);
  }
}

export default ListingCard;
